import React from 'react';
import { Button, Tag, Tooltip } from 'antd';
import { ArrowRightOutlined, TruckOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import type { TransportRouteDetailDto } from '../../../dto/transport';
import { formatDateEuropean } from '../../../utils/dateUtils';
import { getTransportLabel } from '../../../utils/transportLabel';
import { buildTransportRouteLabel } from '../transportAddressUtils';
import { getRowDeliveryDate, getVisibleOrderTransports } from '../orderVisibleTransports';
import './OrderTransportStatusStrip.css';

const STATUS_COLORS: Record<TransportRouteDetailDto['status'], string> = {
  PENDING: 'default',
  SCHEDULED: 'blue',
  LOADING: 'gold',
  IN_TRANSIT: 'processing',
  DELIVERED: 'success',
  CANCELLED: 'error',
  PLANNED: 'default',
  DELAYED: 'error',
};

const EN_ROUTE_STATUSES = new Set<TransportRouteDetailDto['status']>([
  'IN_TRANSIT',
  'DELAYED',
]);

/** Carrier on the first line, transport number on the second; one line when only one is known. */
const getTruckIdentityLines = (transport: TransportRouteDetailDto, fallback: string): string[] => {
  const carrier = transport.transportCompany?.trim() ?? '';
  const number = getTransportLabel(transport);
  if (carrier && number) return [carrier, number];
  return [number || carrier || fallback];
};

interface OrderTransportStatusStripProps {
  transports: TransportRouteDetailDto[];
}

const OrderTransportStatusStrip: React.FC<OrderTransportStatusStripProps> = ({ transports }) => {
  const { t } = useTranslation(['transport']);
  const navigate = useNavigate();
  const visibleTransports = getVisibleOrderTransports(transports);

  if (visibleTransports.length === 0) return null;

  const enRouteCount = visibleTransports.filter((transport) => (
    EN_ROUTE_STATUSES.has(transport.status)
  )).length;

  return (
    <section className="order-transport-status" aria-labelledby="order-transport-status-title">
      <div className="order-transport-status__header">
        <div className="order-transport-status__title" id="order-transport-status-title">
          <TruckOutlined />
          <span>{t('transport:orderTransports.statusStrip.title')}</span>
        </div>
        <span className="order-transport-status__summary">
          {t('transport:orderTransports.statusStrip.enRouteSummary', {
            enRoute: enRouteCount,
            count: visibleTransports.length,
          })}
        </span>
      </div>

      <div className="order-transport-status__list">
        {visibleTransports.map((transport, index) => {
          const route = buildTransportRouteLabel(
            transport.loadingAddresses,
            transport.deliveryAddresses,
            transport.originCity,
            transport.destinationCity,
          );
          const isDelivered = transport.status === 'DELIVERED';
          const deliveryDate = getRowDeliveryDate(transport);
          const truckLines = getTruckIdentityLines(
            transport,
            t('transport:orderTransports.statusStrip.truckNumber', {
              index: index + 1,
              count: visibleTransports.length,
            }),
          );

          return (
            <div className="order-transport-status__row" key={transport.id}>
              <div className="order-transport-status__truck">
                <TruckOutlined aria-hidden />
                <div className="order-transport-status__truck-text">
                  {truckLines.map((line, lineIndex) => (
                    <span
                      key={lineIndex}
                      className={lineIndex === 0
                        ? 'order-transport-status__truck-carrier'
                        : 'order-transport-status__truck-number'}
                      title={typeof line === 'string' ? line : undefined}
                    >
                      {line}
                    </span>
                  ))}
                </div>
              </div>

              <Tag
                bordered={false}
                color={STATUS_COLORS[transport.status]}
                className="order-transport-status__tag"
              >
                {t(`transport:status.${transport.status.toLowerCase()}`)}
              </Tag>

              <Tooltip title={route.tooltip} placement="topLeft">
                <span className="order-transport-status__route">{route.label}</span>
              </Tooltip>

              <span className="order-transport-status__date">
                {deliveryDate
                  ? t(
                      isDelivered
                        ? 'transport:orderTransports.statusStrip.deliveredAt'
                        : 'transport:orderTransports.statusStrip.plannedDelivery',
                      { date: formatDateEuropean(deliveryDate) },
                    )
                  : t('transport:orderTransports.statusStrip.deliveryDateMissing')}
              </span>

              <Button
                type="link"
                className="order-transport-status__link"
                onClick={() => navigate(`/app/transports/${encodeURIComponent(transport.id)}`)}
              >
                {t('transport:orderTransports.statusStrip.openTransport')}
                <ArrowRightOutlined aria-hidden />
              </Button>
            </div>
          );
        })}
      </div>
    </section>
  );
};

export default OrderTransportStatusStrip;
